/**
 * Colour Match click-through attribution.
 *
 * WHY THIS EXISTS. The full tool (sections/material-colour-match.liquid) adds
 * to cart itself, and stamps the cart with colour_match_source /
 * colour_match_input / colour_match_spool just before it does. The product
 * page callout and the feature page band have no add to cart: their result
 * rows are links out to the product. So a sale those surfaces caused set no
 * cart attribute, landed on the order with no note attribute, and was invisible
 * to the order tagging Flow — the tool got no credit for any sale it did not
 * complete itself.
 *
 * This stamps the cart at the moment the shopper follows a match to a product,
 * which is the last point those surfaces are involved. It over-attributes
 * slightly: following a link is intent, not a purchase, and the attributes
 * persist on the cart for the session. Treat the order tag as "Colour Match
 * influenced this order", not "Colour Match sold this line". The per-line
 * _colour_match property the full tool writes remains the precise signal.
 *
 * Any element with [data-cm-surface] is a surface; a click on a product link
 * inside one is a click-through. Nothing here needs a list of surfaces. The
 * one exception is the full tool, surface "page": it stamps the cart itself,
 * per spool, with the Made in USA variant resolved, so it is skipped here.
 *
 * The typed colour is read from the surface's own input,
 * [data-jessie-match-input], which is jessie-match.js's contract for that
 * field; with nothing typed on this surface the gate's stored value is used,
 * since the shopper may have typed on another surface this session. Neither
 * source ever holds a placeholder or default, so "no value" really does mean
 * no colour was entered, and no stamp is sent.
 *
 * keepalive lets the POST outlive the navigation it races. Without it the
 * browser cancels the request as the next page starts loading and the stamp is
 * lost perhaps half the time. An engine without keepalive gets the same
 * request through sendBeacon, form-encoded, which /cart/update.js also accepts.
 *
 * Idempotent: the callout and the band both emit this src, and a repeated src
 * re-executes. Hence the guard.
 */
(function () {
  'use strict';

  if (window.__cm_attribution) return;
  window.__cm_attribution = true;

  var PRODUCT_RE = /\/products\/([^?#\/]+)/;
  var KEEPALIVE = (function () {
    try {
      return new Request('/', { keepalive: true }).keepalive === true;
    } catch (e) {
      return false;
    }
  })();

  function typedIn(surface) {
    var input = surface.querySelector('[data-jessie-match-input]');
    var value = input && input.value ? String(input.value).trim() : '';
    if (value) return value;
    if (window.JessieGate && typeof window.JessieGate.input === 'function') {
      return String(window.JessieGate.input() || '').trim();
    }
    return '';
  }

  function stamp(surfaceName, typed, handle) {
    var attributes = {
      colour_match_source: surfaceName,
      colour_match_input: typed,
      colour_match_spool: handle || ''
    };
    try {
      if (KEEPALIVE) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          keepalive: true,
          body: JSON.stringify({ attributes: attributes })
        }).catch(function () {});
      } else if (navigator.sendBeacon) {
        var form = new URLSearchParams();
        for (var key in attributes) {
          if (Object.prototype.hasOwnProperty.call(attributes, key)) {
            form.append('attributes[' + key + ']', attributes[key]);
          }
        }
        navigator.sendBeacon('/cart/update.js', form);
      }
    } catch (e) {
      /* Attribution must never block the navigation the shopper asked for. */
    }
  }

  function onClick(event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    var surface = link.closest('[data-cm-surface]');
    if (!surface) return;

    var name = surface.getAttribute('data-cm-surface') || 'unknown';
    /* The full page stamps the cart itself, per spool, at add-to-cart. */
    if (name === 'page') return;

    var match = PRODUCT_RE.exec(link.getAttribute('href') || '');
    if (!match) return;

    var typed = typedIn(surface);
    if (!typed) return; /* No colour was ever entered: not a Colour Match journey. */

    stamp(name, typed, match[1]);

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'colour_match_click',
        surface: name,
        input_value: typed,
        /* The MCM row id jessie-match.js writes on the row, so this agrees
           with matched_spool on colour_match; the handle is the fallback. */
        matched_spool: link.getAttribute('data-jessie-match-row') || match[1]
      });
    } catch (e) {}
  }

  document.addEventListener('click', onClick, true);
  /* Middle click opens a new tab; it is still a click-through. */
  document.addEventListener('auxclick', function (event) {
    if (event.button === 1) onClick(event);
  }, true);
})();
